import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pencil, Plus, Users } from "lucide-react"

import { DataState } from "@/components/DataState"
import { ReasonDialog } from "@/components/ReasonDialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useToast } from "@/components/ui/use-toast"
import { useRbac } from "@/lib/auth"
import { useI18n, t } from "@/lib/i18n"
import { formatPercent } from "@/lib/format"
import type { Supplier } from "@/lib/types"
import { cn } from "@/lib/utils"
import { onWriteError } from "@/lib/writeErrors"

import { FieldsDialog, type FieldSpec, type FieldValue } from "@/routes/masters/FieldsDialog"
import { ContactosDialog } from "@/routes/masters/ContactosDialog"
import { fetchSuppliers, saveSupplier } from "@/routes/masters/api"
import { fetchLineasDeLaHoja } from "@/routes/finance/api"

const SIN_LINEA = "__sin_linea__"

/*
  LO QUE SE QUITÓ DE ESTA PANTALLA, Y POR QUÉ. Tenía una columna «Cuenta
  habitual» y un campo para elegirla de una lista de seis cuentas fijas, y una
  «Política de pago». La cuenta habitual del proveedor es la MEMORIA del
  clasificador (`classify`), que se escribe sola con cada corrección en la cola
  de revisión: aquí siempre salía vacía y lo que se elegía no se guardaba. La
  política de pago no la lee ningún módulo fuera del propio maestro.
*/

export function SuppliersPage() {
  const { can } = useRbac()
  const { term } = useI18n()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [editing, setEditing] = React.useState<Supplier | null>(null)
  const [creating, setCreating] = React.useState(false)
  // Activar / desactivar: ver el comentario gemelo en `ClientsPage`.
  const [cambiandoEstado, setCambiandoEstado] = React.useState<Supplier | null>(null)
  const [mostrarInactivos, setMostrarInactivos] = React.useState(false)
  const [contactosDe, setContactosDe] = React.useState<Supplier | null>(null)

  const suppliersQuery = useQuery({ queryKey: ["suppliers"], queryFn: fetchSuppliers })
  // LAS LÍNEAS DE COSTO Y DE GASTO de la hoja, para decir en cuál cae el
  // proveedor. Ver el comentario gemelo en `ClientsPage`.
  const lineasCosto = useQuery({ queryKey: ["lineas-hoja", "COGS"], queryFn: () => fetchLineasDeLaHoja("Cost of Goods Sold (COGS)") })
  const lineasGasto = useQuery({ queryKey: ["lineas-hoja", "G&A"], queryFn: () => fetchLineasDeLaHoja("G&A Expenses") })

  const mutation = useMutation({
    mutationFn: (payload: { values: Record<string, FieldValue>; reason: string; id?: string }) =>
      saveSupplier({
        data: {
          ...(payload.values as Partial<Supplier>),
          lineaHoja: payload.values.lineaHoja === SIN_LINEA ? "" : String(payload.values.lineaHoja ?? ""),
          id: payload.id,
        },
        reason: payload.reason,
      }),
    onSuccess: () => {
      toast({
        title: "Proveedor guardado",
        description: "El cambio quedo versionado con motivo.",
      })
      setEditing(null)
      setCreating(false)
      setCambiandoEstado(null)
      void queryClient.invalidateQueries({ queryKey: ["suppliers"] })
    },
    onError: onWriteError("el proveedor"),
  })

  const todos = suppliersQuery.data ?? []
  const inactivos = todos.filter((p) => !p.activo).length
  const suppliers = mostrarInactivos ? todos : todos.filter((p) => p.activo)

  const fields: FieldSpec[] = [
    { type: "text", name: "name", label: "Razon social", required: true, wide: true },
    { type: "text", name: "ruc", label: "RUC", required: true },
    {
      type: "select",
      name: "currency",
      label: "Moneda",
      required: true,
      options: [
        { value: "PEN", label: "PEN - Soles" },
        { value: "USD", label: "USD - Dolares" },
      ],
    },
    { type: "number", name: "paymentTermsDays", label: "Plazo de pago (dias)", required: true },
    {
      /* Cesar: «si es prorrateado por mes o si es un one shot». */
      type: "select",
      name: "modalidadGasto",
      label: "Como se paga",
      options: [
        { value: "mensual", label: "Mensual (prorrateado)" },
        { value: "one_shot", label: "Una vez (one shot)" },
      ],
      help: "Mensual: un gasto que se repite todos los meses. One shot: una compra puntual.",
    },
    { type: "number", name: "diaDePago", label: "Dia del mes en que se le paga" },
    { type: "text", name: "enlaceContrato", label: "Enlace al contrato", placeholder: "https://…", wide: true },
    { type: "text", name: "detractionCode", label: "Codigo de detraccion", placeholder: "022" },
    {
      type: "number",
      name: "detractionRate",
      label: "Tasa de detraccion (0-1)",
      placeholder: "0.12",
    },
    { type: "checkbox", name: "retentionApplies", label: "Sujeto a retencion de 3%" },
    {
      type: "select",
      name: "lineaHoja",
      label: "Línea de la hoja",
      wide: true,
      help: "En qué línea de costo de ventas o de gastos de tu hoja cae lo que factura. Con esto Finanzas › Desglose abre la línea por proveedor.",
      options: [
        { value: SIN_LINEA, label: "Sin línea (no se abre en el desglose)" },
        ...(lineasCosto.data?.lineas ?? []).map((l) => ({ value: l, label: `Costo de ventas · ${l}` })),
        ...(lineasGasto.data?.lineas ?? []).map((l) => ({ value: l, label: `Gastos · ${l}` })),
      ],
    },
  ]

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <p className="max-w-[80ch] text-xs leading-[1.15rem] text-muted-foreground">
          {t("A quién le compras, con qué plazo y con qué retención o detracción. La cuenta contable de cada proveedor la aprende el motor con cada corrección en Revisión.")}</p>
        <div className="flex shrink-0 items-center gap-3">
          <label className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <Checkbox
              checked={mostrarInactivos}
              onCheckedChange={(v) => setMostrarInactivos(v === true)}
            />
            {t("Mostrar inactivos")}{inactivos ? ` (${inactivos})` : ""}
          </label>
          <Button size="sm" disabled={!can("edit")} onClick={() => setCreating(true)}>
            <Plus />
            {t("Nuevo proveedor")}</Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-panel border bg-surface shadow-xs">
        <DataState
          loading={suppliersQuery.isLoading}
          error={suppliersQuery.error}
          onRetry={() => void suppliersQuery.refetch()}
          empty={suppliers.length === 0}
          emptyMessage={t("Todavia no hay proveedores en el maestro.")}
          emptyHint={t("Los proveedores se dan de alta solos con el primer comprobante que llega. Registrarlos antes con su detraccion y retencion acelera la clasificacion.")}
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("Proveedor")}</TableHead>
                <TableHead className="w-32">{term.identificador_fiscal}</TableHead>
                <TableHead className="w-20">{t("Moneda")}</TableHead>
                <TableHead className="w-24 text-right">{t("Plazo (d)")}</TableHead>
                <TableHead className="w-24">{t("Retencion")}</TableHead>
                <TableHead className="w-32">{t("Detraccion")}</TableHead>
                <TableHead className="w-28">{t("Como se paga")}</TableHead>
                <TableHead className="w-20">{t("Estado")}</TableHead>
                <TableHead className="w-56" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {suppliers.map((supplier) => {
                return (
                  <TableRow key={supplier.id} className={cn(!supplier.activo && "text-muted-foreground")}>
                    <TableCell className="font-medium">{supplier.name}</TableCell>
                    <TableCell className="code">{supplier.ruc}</TableCell>
                    <TableCell>{supplier.currency}</TableCell>
                    <TableCell className="num">{supplier.paymentTermsDays}</TableCell>
                    <TableCell>
                      {supplier.retentionApplies ? (
                        <Badge variant="warning-soft">3%</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-2xs">
                      {supplier.detractionCode ? (
                        <>
                          {t("cod.")}{" "}{supplier.detractionCode} ·{" "}
                          {formatPercent(supplier.detractionRate ?? 0, 0)}
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-2xs">
                      {supplier.modalidadGasto === "mensual" ? t("Mensual")
                        : supplier.modalidadGasto === "one_shot" ? t("Una vez")
                        : <span className="text-muted-foreground">—</span>}
                      {supplier.enlaceContrato ? (
                        <a href={supplier.enlaceContrato} target="_blank" rel="noreferrer"
                           className="ml-1 text-brand underline-offset-2 hover:underline">{t("contrato")}</a>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Badge variant={supplier.activo ? "success-soft" : "outline"} estado>
                        {supplier.activo ? t("activo") : t("inactivo")}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button size="xs" variant="ghost" onClick={() => setContactosDe(supplier)} title={t("Contactos")}>
                        <Users />
                        {supplier.contactos || ""}
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!can("edit")}
                        onClick={() => setCambiandoEstado(supplier)}
                      >
                        {supplier.activo ? t("Desactivar") : t("Activar")}</Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={!can("edit")}
                        onClick={() => setEditing(supplier)}
                      >
                        <Pencil />
                        {t("Editar")}</Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </DataState>
      </div>

      {contactosDe ? (
        <ContactosDialog
          open
          onOpenChange={(v) => { if (!v) setContactosDe(null) }}
          partyId={contactosDe.id}
          nombre={contactosDe.name}
          puedeEditar={can("edit")}
        />
      ) : null}

      {cambiandoEstado ? (
        <ReasonDialog
          open
          onOpenChange={(v) => { if (!v) setCambiandoEstado(null) }}
          title={`${cambiandoEstado.activo ? t("Desactivar") : t("Activar")} · ${cambiandoEstado.name}`}
          description={cambiandoEstado.activo
            ? t("No se borra nada: conserva sus comprobantes y su memoria de clasificación. Deja de ofrecerse para lo nuevo.")
            : t("Vuelve a ofrecerse para lo nuevo.")}
          confirmLabel={cambiandoEstado.activo ? t("Desactivar") : t("Activar")}
          variant={cambiandoEstado.activo ? "destructive" : "default"}
          suggestions={cambiandoEstado.activo
            ? ["Dejamos de comprarle.", "Proveedor duplicado: se usa la otra ficha."]
            : ["Volvimos a comprarle."]}
          pending={mutation.isPending}
          onConfirm={(reason) =>
            mutation.mutateAsync({
              values: { activo: !cambiandoEstado.activo }, reason, id: cambiandoEstado.id,
            }).then(() => undefined)}
        />
      ) : null}

      <FieldsDialog
        open={Boolean(editing) || creating}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setCreating(false)
          }
        }}
        title={editing ? `Editar proveedor · ${editing.name}` : "Nuevo proveedor"}
        fields={fields}
        initial={
          editing
            ? {
                name: editing.name,
                ruc: editing.ruc,
                currency: editing.currency,
                paymentTermsDays: editing.paymentTermsDays,
                detractionCode: editing.detractionCode,
                detractionRate: editing.detractionRate,
                retentionApplies: editing.retentionApplies,
                modalidadGasto: editing.modalidadGasto ?? undefined,
                diaDePago: editing.diaDePago ?? undefined,
                enlaceContrato: editing.enlaceContrato,
                lineaHoja: editing.lineaHoja || SIN_LINEA,
              }
            : { currency: "PEN", paymentTermsDays: 30, retentionApplies: false, lineaHoja: SIN_LINEA }
        }
        pending={mutation.isPending}
        reasonSuggestions={["Actualizacion de condiciones comerciales.", "Alta de proveedor nuevo."]}
        onSubmit={(values, reason) =>
          mutation.mutateAsync({ values, reason, id: editing?.id }).then(() => undefined)
        }
      />
    </div>
  )
}
